import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

// Thunk gửi thông tin cấu hình kết nối lên server
export const saveConnectionConfig = createAsyncThunk("caidat/saveConnectionConfig", async (config, { rejectWithValue }) => {
  try {
    const response = await api.post("/api/caidat/connection", config);
    return response.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Thunk liên thông đơn thuốc
export const lienThongDonThuoc = createAsyncThunk("caidat/lienThongDonThuoc", async (ids, { rejectWithValue }) => {
  try {
    const response = await api.post("/object/medeco.prescription/action_neps_create_and_send", { ids });
    return response.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Thunk cập nhật thông tin chi nhánh
export const updateOperatingUnit = createAsyncThunk("caidat/updateOperatingUnit", async (data, { rejectWithValue }) => {
  try {
    const operatingUnitId = localStorage.getItem("selected_operating_unit_id");
    if (!operatingUnitId) {
      throw new Error("Không tìm thấy ID chi nhánh trong localStorage");
    }
    const response = await api.put(`/operating.unit/${operatingUnitId}`, data);

    // Cập nhật lại thông tin trong localStorage
    try {
      const companyRaw = localStorage.getItem("company");
      if (companyRaw) {
        const companyList = JSON.parse(companyRaw);
        if (Array.isArray(companyList)) {
          const updatedList = companyList.map((c) => (String(c.id) === String(operatingUnitId) ? { ...c, ...response.data } : c));
          localStorage.setItem("company", JSON.stringify(updatedList));

          // Cập nhật selected_operating_unit nếu đang chọn chi nhánh này
          const updatedUnit = updatedList.find((c) => String(c.id) === String(operatingUnitId));
          if (updatedUnit) {
            localStorage.setItem("selected_operating_unit", JSON.stringify(updatedUnit));
          }
        }
      }
    } catch (err) {
      console.error("Error updating localStorage:", err);
    }

    return response.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Thunk lấy dữ liệu biểu đồ tháng cho trang tổng quan
export const getMonthChart = createAsyncThunk("caidat/getMonthChart", async (_, { rejectWithValue }) => {
  try {
    //add operating_unit_id param from localStorage
    const operatingUnitId = localStorage.getItem("selected_operating_unit_id");
    const response = await api.get("/report/month-chart", { params: { operating_unit_id: operatingUnitId } });
    console.log("Month chart data:", response.data);
    return response.data || { current_month: {}, previous_month: {} };
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Thunk lấy danh sách tỉnh/thành phố Việt Nam
export const fetchProvinces = createAsyncThunk("caidat/fetchProvinces", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/res.country.state", { params: { "country_id.code": "VN" } });
    return res.data?.content || res.data || [];
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Thunk lấy danh sách xã/phường Việt Nam
export const fetchWards = createAsyncThunk("caidat/fetchWards", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/res.country.ward", { params: { "country_id.code": "VN" } });
    return res.data?.content || res.data || [];
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

const caidatSlice = createSlice({
  name: "caidat",
  initialState: {
    connectionConfig: null,
    saveStatus: "idle",
    saveError: null,
    lienThongStatus: "idle",
    lienThongError: null,
    updateOperatingUnitStatus: "idle",
    updateOperatingUnitError: null,
    currentMonth: {},
    previousMonth: {},
    today: {},
    monthChartError: null,
    provinces: [],
    wards: [],
    provincesError: null,
    wardsError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(saveConnectionConfig.pending, (state) => {
        state.saveStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveConnectionConfig.fulfilled, (state, action) => {
        state.saveStatus = "succeeded";
        state.connectionConfig = action.payload;
      })
      .addCase(saveConnectionConfig.rejected, (state, action) => {
        state.saveStatus = "failed";
        state.saveError = action.payload;
      })
      .addCase(lienThongDonThuoc.pending, (state) => {
        state.lienThongStatus = "loading";
        state.lienThongError = null;
      })
      .addCase(lienThongDonThuoc.fulfilled, (state) => {
        state.lienThongStatus = "succeeded";
      })
      .addCase(lienThongDonThuoc.rejected, (state, action) => {
        state.lienThongStatus = "failed";
        state.lienThongError = action.payload;
      })
      .addCase(updateOperatingUnit.pending, (state) => {
        state.updateOperatingUnitStatus = "loading";
        state.updateOperatingUnitError = null;
      })
      .addCase(updateOperatingUnit.fulfilled, (state, action) => {
        state.updateOperatingUnitStatus = "succeeded";
        state.operatingUnit = action.payload;
      })
      .addCase(updateOperatingUnit.rejected, (state, action) => {
        state.updateOperatingUnitStatus = "failed";
        state.updateOperatingUnitError = action.payload;
      });
    builder.addCase(getMonthChart.fulfilled, (state, action) => {
      state.visitData = "succeeded";
      state.currentMonth = action.payload.current_month || [];
      state.previousMonth = action.payload.previous_month || [];
      state.today = action.payload.today || {};
      state.monthChartError = null;
    });
    builder
      .addCase(fetchProvinces.fulfilled, (state, action) => {
        state.provinces = Array.isArray(action.payload) ? action.payload : [];
        state.provincesError = null;
      })
      .addCase(fetchProvinces.rejected, (state, action) => {
        state.provinces = [];
        state.provincesError = action.payload || "Failed to load provinces";
      });
    builder
      .addCase(fetchWards.fulfilled, (state, action) => {
        state.wards = Array.isArray(action.payload) ? action.payload : [];
        state.wardsError = null;
      })
      .addCase(fetchWards.rejected, (state, action) => {
        state.wards = [];
        state.wardsError = action.payload || "Failed to load wards";
      });
  },
});

export default caidatSlice.reducer;
